"use client";

import Image from "next/image";
import CustomButton from "./CustomButton";
import AnimateOnScroll from "./AnimateOnScroll";
import CallExpandBtn from "./CallExpandBtn";
import AlexARLogo from "../icons/AlexARLogo";
import { ThemeName, THEME_HEX } from "./theme";
import { SITE_URL, ORGANIZATION_NAME } from "@/lib/metadata";
import { ROUTES } from "@/lib/routes";
import { trackBookAppointmentClicked } from "@/lib/analytics";

type CTATheme = "blue" | "yellow" | "red" | "pink";

// Service types for deep linking to booking flow
type BookingServiceType =
  | "counselling"
  | "life-coach"
  | "occupational-therapy"
  | "psychology";

interface CTAProps {
  theme?: Exclude<ThemeName, "white">;
  title?: string;
  subtitle?: string;
  imagePath?: string;
  /** Optional service type to pre-select in booking flow */
  bookingService?: BookingServiceType;
}

const defaultTitle = "You Don’t Have to Figure It Out Alone";
const defaultSubtitle =
  "Whether you need counselling, coaching, allied health support, or self-guided learning, our team is here to help you take the next step with clarity and care.";

const CTA = ({
  theme = "blue",
  title = defaultTitle,
  subtitle = defaultSubtitle,
  imagePath = "/images/v3/home/cta-tensed.png",
  bookingService,
}: CTAProps) => {
  // Generate booking URL with optional service pre-selection
  const bookingUrl = bookingService
    ? `${ROUTES.booking}?service=${bookingService}`
    : ROUTES.booking;

  const reserveActionSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: title,
    description: subtitle,
    provider: {
      "@type": "Organization",
      name: ORGANIZATION_NAME,
      url: SITE_URL,
    },
    potentialAction: {
      "@type": "ReserveAction",
      name: "Book an Appointment",
      target: {
        "@type": "EntryPoint",
        urlTemplate: new URL(bookingUrl, SITE_URL).href,
        actionPlatform: [
          "https://schema.org/DesktopWebPlatform",
          "https://schema.org/MobileWebPlatform",
        ],
      },
    },
  };

  return (
    <section
      className="relative h-[600px] w-full overflow-hidden bg-white lg:h-[791px]"
      aria-labelledby="cta-heading"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(reserveActionSchema).replace(/</g, "\\u003c"),
        }}
      />
      {/* ── Background image (centered, 1427px wide) with edge + bottom fades ── */}
      <div className="absolute inset-y-0 left-1/2 h-full w-[1427px] -translate-x-1/2">
        <Image
          src={imagePath}
          alt=""
          fill
          className="object-cover object-center"
          sizes="1427px"
          aria-hidden
        />
        {/* Top fade → white */}
        <div className="absolute left-0 right-0 top-0 h-[100px] bg-gradient-to-b from-white to-transparent" />
        {/* Bottom fade → white */}
        <div className="absolute bottom-0 left-0 right-0 h-[390px] bg-gradient-to-t from-white via-white/80 to-transparent" />
        {/* Left fade → white */}
        <div className="absolute bottom-0 left-0 top-0 w-[246px] bg-gradient-to-r from-white to-transparent" />
        {/* Right fade → white */}
        <div className="absolute bottom-0 right-0 top-0 w-[246px] bg-gradient-to-l from-white to-transparent" />
      </div>

      {/* ── Content ── */}
      <div className="relative z-10 mx-auto flex h-full max-w-[1373px] flex-col items-center justify-end px-5 pb-10 text-center lg:pb-16">
        <AnimateOnScroll className="flex flex-col items-center gap-6 text-center lg:gap-8">
          {/* Logo mark */}
          <AlexARLogo height={76} width={90} color={THEME_HEX[theme]} />

          {/* Heading */}
          <h2
            id="cta-heading"
            className={`font-crimsonText font-bold leading-none ${theme === "blue" ? "text-v3-secondary" : "text-black"}`}
            style={{ fontSize: "clamp(30px, 3.5vw, 80px)" }}
          >
            {title}
          </h2>

          {/* Paragraph */}
          <p
            className={`max-w-[1353px] font-onest font-normal leading-[130%] ${theme === "blue" ? "text-v3-body" : "text-[#747474]"}`}
            style={{ fontSize: "clamp(18px, 1.875vw, 28px)" }}
          >
            {subtitle}
          </p>

          {/* CTAs */}
          <div
            className="flex flex-col items-center gap-3 sm:flex-row sm:gap-5"
            role="group"
            aria-label="Contact options"
          >
            <CallExpandBtn theme={theme} />
            <CustomButton
              label="Book an Appointment"
              href={bookingUrl}
              variant="primary"
              withIcon
              size="lg"
              theme={theme}
              onClick={() =>
                trackBookAppointmentClicked({
                  location: "cta",
                  service: bookingService,
                })
              }
            />
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
};

export default CTA;
